import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Image from 'next/image';
import urljoin from 'url-join';
import cn from 'classnames';

import Link from '@/components/link';
import Layout from '@/components/Layout/Vanilla';
import { fetchPage, gql } from '@/lib/graphcms';
import Card from '@/components/blog/card';
import Heading from '@/components/Heading';
import Pagination from '@/components/blog/pagination';
import Badge from '@/components/blog/badge';
import SlackCard from '@/components/blog/slack-card';
import EnterpriseCard from '@/components/blog/enterprise-card';
import styles from '@/styles/pages/blog-index.module.css';
import { Page, Post } from '@/types';
import { truncateText } from '@/utils/resolvers';
import { formatDate } from '@/utils/date';
import Authors from '@/components/blog/authors';
import CardsContainer from '@/components/blog/cards-container';

import academyCardImage from '@/public/images/blog/academy-card.png';
import communityCardImage from '@/public/images/blog/community-card.png';
import documentationCardImage from '@/public/images/blog/documentation-card.png';
import writeCardImage from '@/public/images/blog/write-card.png';
import Button from '@/components/Button';
import { getPagination } from '@/utils/blog';
import { defaultSeoUrl } from '../../../next-seo.config';

type BlogProps = {
  page: Page;
  blogPostsConnection: {
    blogPosts: {
      node: Post;
    }[];
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
    aggregate: {
      count: number;
    };
  };
  featuredPost: {
    slug: string;
    title: string;
    subTitle: string;
    coverImage: {
      alt: string;
      url: string;
    };
    categories: {
      id: string;
      title: string;
      slug: string;
    }[];
    publishedOn: string;
    authors: {
      name: string;
      photo: {
        url: string;
        alt: string;
      };
    }[];
  };
  categories: {
    title: string;
    slug: string;
  }[];
  currentPage: number;
  isFirstPage: boolean;
  preview: boolean;
};

// The limit is 10 because we remove the featured post from the list
const limit = 10;

const gridLinks = [
  {
    href: `/academy`,
    cta: `Learn more`,
    title: `Academy`,
    description: `Learn the basics about Headless CMS, GraphQL, DXP and more.`,
    color: `bg-indigo-rainbow hover:bg-zodiac`,
    image: academyCardImage,
  },
  {
    href: `/community`,
    cta: `Check them out`,
    title: `Community`,
    description: `See what the GraphCMS community is building and share your own.`,
    color: `bg-pink-600 hover:bg-pink-700`,
    image: communityCardImage,
  },
  {
    href: `/docs`,
    cta: `Explore Docs`,
    title: `Documentation`,
    description: `Get started with your project with our API and SDK documentation.`,
    color: `bg-green-600 hover:bg-green-700`,
    image: documentationCardImage,
  },
  {
    href: `/write-for-graphcms`,
    cta: `Create content`,
    title: `Write for GraphCMS`,
    description: `Share your technical expertise and help other developers learn.`,
    color: `bg-spanish-yellow hover:bg-yellow-600`,
    image: writeCardImage,
  },
];

export default function Blog({
  page,
  blogPostsConnection: { blogPosts, aggregate, pageInfo },
  featuredPost,
  categories,
  currentPage,
  isFirstPage,
  preview,
}: BlogProps) {
  const router = useRouter();

  const pageUrl = urljoin(defaultSeoUrl, router.asPath);
  const pageTitle = !isFirstPage
    ? `Page ${currentPage} | ${page.title}`
    : page.title;

  return (
    <>
      <Head>
        {pageInfo.hasPreviousPage && (
          <link rel="prev" href={`/blog?page=${currentPage - 1}`} />
        )}
        {pageInfo.hasNextPage && (
          <link rel="next" href={`/blog?page=${currentPage + 1}`} />
        )}
        {!isFirstPage && <link rel="canonical" href={pageUrl} />}
      </Head>

      <Layout
        preview={preview}
        seo={{
          ...page,
          meta: {
            ...page.meta,
            title: pageTitle,
          },
        }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <Heading>Blog</Heading>

          <div className="mt-6 space-y-3 lg:space-y-0">
            {categories.length > 0 &&
              categories.map((category) => (
                <Badge
                  key={category.slug}
                  slug={`/blog/tag/${category.slug}`}
                  title={category.title}
                  variant="default"
                  className="mr-3 lg:mr-2"
                />
              ))}
          </div>

          <section className="mt-10 flex items-center justify-between flex-col md:flex-row">
            <div className="w-full blog-image">
              <Link
                href={`/blog/${featuredPost.slug}`}
                className="blog-card-image"
              >
                {featuredPost?.coverImage?.url && (
                  <Image
                    width={740}
                    height={422}
                    src={featuredPost.coverImage.url}
                    alt={featuredPost.coverImage.alt}
                    className="rounded"
                    objectFit="cover"
                    layout="responsive"
                    priority
                  />
                )}
              </Link>
            </div>

            <div className="w-full md:max-w-sm sm:flex-col md:ml-10 lg:ml-20 mt-4 md:mt-0 blog-card-text">
              <div className="mb-4">
                <Badge as="text" title="Featured" variant="primary" />
              </div>
              <Link
                href={`/blog/${featuredPost.slug}`}
                className="space-y-4 hover:text-cornflower transition-colors blog-card-title"
              >
                <h2 className="text-[22px] md:text-3xl leading-tight font-bold">
                  {featuredPost.title}
                </h2>
                <p className="text-light-haiti leading-normal">
                  {truncateText({ text: featuredPost.subTitle })}
                </p>
              </Link>
              <div className="mt-8 flex justify-between items-center">
                <Authors
                  authors={featuredPost.authors}
                  type="large"
                  showAuthorsName
                />

                <div className="h-full text-xs text-zodiac uppercase ml-2">
                  <time
                    dateTime={featuredPost.publishedOn}
                    className="h-full flex items-center pt-[1px]"
                  >
                    {formatDate(featuredPost.publishedOn)}
                  </time>
                </div>
              </div>
            </div>
          </section>

          <CardsContainer className="mt-20">
            {blogPosts.map(({ node }) => (
              <Card key={node.id} post={node} />
            ))}
          </CardsContainer>

          <div className="mt-20">
            <Pagination
              currentPage={currentPage}
              totalCount={aggregate.count}
              pageSize={limit}
              onPageChange={(pageNumber) => {
                router.push(`/blog?page=${pageNumber}`);
              }}
            />
          </div>

          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-20">
            {gridLinks.map(
              ({ title, href, image, color, description, cta }) => (
                <Link
                  href={href}
                  key={href}
                  className={cn(
                    `grid w-full h-full rounded-lg py-6 px-5 text-whisper transition-colors`,
                    color,
                  )}
                >
                  <div>
                    <h3 className="font-semibold text-xl">{title}</h3>
                    <p className="text-xs mt-2 font-medium">{description}</p>
                    <Button
                      withArrow
                      appearance="minimal"
                      colour="whisper"
                      size="small"
                      className="text-xs font-semibold mt-3 leading-3"
                    >
                      {cta}
                    </Button>
                  </div>
                  <div className="mt-6">
                    <Image
                      src={image}
                      layout="responsive"
                      width={226}
                      height={157}
                      objectFit="contain"
                    />
                  </div>
                </Link>
              ),
            )}
          </div>

          <div className={cn(`mt-20 mb-12 lg:mb-36`, styles.cardsFooter)}>
            <SlackCard />
            <EnterpriseCard />
          </div>
        </div>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  preview = false,
  params,
  res,
  query,
}) => {
  const { page, offset } = getPagination({
    queryPage: query.page,
    limit,
  });

  const {
    blogPostsConnection,
    page: pageData,
    featuredPosts,
    categories,
  } = (await fetchPage(
    gql`
      fragment categoryData on Category {
        id
        slug
        title
      }

      fragment blogPostData on BlogPostEdge {
        node {
          id
          slug
          title
          subTitle
          coverImage {
            ...assetDataXL
          }
          authors {
            ...authorData
          }
          categories {
            ...categoryData
          }
          publishedOn
        }
      }

      query ($limit: Int!, $offset: Int!) {
        blogPostsConnection(
          orderBy: publishedOn_DESC
          first: $limit
          skip: $offset
        ) {
          blogPosts: edges {
            ...blogPostData
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          aggregate {
            count
          }
        }
        featuredPosts: blogPosts(
          orderBy: publishedOn_DESC
          where: { featured: true }
          first: 1
        ) {
          title
          slug
          subTitle
          coverImage {
            alt
            url
          }
          categories {
            id
            title
            slug
          }
          publishedOn
          authors {
            name
            photo {
              url
              alt
            }
          }
        }
        categories(where: { blogPosts_some: {} }) {
          title
          slug
        }
        page: page(where: { slug: "blog" }) {
          ...pageData
        }
      }
    `,
    {
      preview,
      variables: {
        slug: params?.slug,
        limit,
        offset,
      },
    },
  )) || [];

  // If the page param is bigger than the total number of pages, redirect to /blog
  const totalNumberOfPages = Math.ceil(
    blogPostsConnection.aggregate.count / limit,
  );

  if (page > totalNumberOfPages) {
    return {
      redirect: {
        destination: `/blog`,
        permanent: false,
      },
    };
  }

  const isFirstPage = page === 1;
  const featuredPost = isFirstPage
    ? featuredPosts[0]
    : blogPostsConnection.blogPosts[0].node;

  // find the featured post based on the slug and remove it from the list
  const featuredPostIndex = blogPostsConnection.blogPosts.findIndex(
    (post: { node: Post }) => post.node.slug === featuredPost.slug,
  );

  if (featuredPostIndex !== -1) {
    blogPostsConnection.blogPosts.splice(featuredPostIndex, 1);
  }

  res.setHeader(`Cache-Control`, `s-maxage=3600, stale-while-revalidate`);

  return {
    props: {
      preview,
      page: pageData,
      blogPostsConnection,
      currentPage: page,
      featuredPost,
      categories,
      isFirstPage,
    },
  };
};
